import React, { useCallback, useMemo, useRef, useState, } from 'react';
import { View, TextInput, TouchableWithoutFeedback, StyleSheet, } from 'react-native';
import { TextInputMask, } from 'react-native-masked-text';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { rem, hitSlop } from 'design-system/values';
import { IconClose } from 'design-system/icons/IconClose';
import { IconArrow } from 'design-system/icons/IconArrow';
import { Switch } from 'design-system/Switch';
import { SettingsItemIcon } from 'design-system/SettingsItemIcon';
import Text from 'design-system/Text';
import Squircle from 'design-system/Squircle';
import IconCheck from 'design-system/icons/IconCheck';
import BlurAmountView from 'design-system/BlurAmountView';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { isDesktop, isIOS, isWeb } from '../constants';
import Indicator from './Indicator';
import RadioButton from './RadioButton';
export const SettingsItemContent = ({ children, style, ...props }) => {
    const styles = useStyles(settingsItemStyleSet);
    return (<View style={[styles.contentContainer, style]} {...props}>
      {children}
    </View>);
};
export const SettingsItemRightContent = ({ children, style, ...props }) => {
    const styles = useStyles(settingsItemStyleSet);
    return (<View style={[styles.rightContentContainer, style]} {...props}>
      {children}
    </View>);
};
export const SettingsItemTitle = (props) => (<Text {...props} {...(props.useNumbersFont ? { 'Numbers-16': true } : { 'Text-16': true })}/>);
export const SettingsItemDescription = (props) => (<Text TextThin-14 Gray {...props}/>);
export const SettingsItemSubDescription = (props) => (<Text TextThin-14 Gray {...props}/>);
export const SettingsItemBase = React.memo(({ light, title, children, selected, renderIcon, description, topDescription, disabled, icon: iconNode, iconSize = 44, onPress, style, descriptionStyle, descriptionProps, titleProps, }) => {
    const childrenArray = React.Children.toArray(children);
    const content = childrenArray.find((node) => node?.type === SettingsItemContent);
    const rightContent = childrenArray.find((node) => node?.type === SettingsItemRightContent);
    const colors = useColors();
    const styles = useStyles(settingsItemStyleSet);
    return (<TouchableHighlight onCard hoverOnLight activeOpacity={1} onPress={onPress} disabled={!!disabled || !onPress} underlayColor={colors.background.underlay}>
        <View style={[styles.container, style]}>
          {!!renderIcon && renderIcon()}
          {iconNode ? (<View style={[styles.iconContainer, { maxWidth: rem(iconSize) }]}>
              <SettingsItemIcon iconSize={iconSize} light={light}>
                {iconNode}
              </SettingsItemIcon>
              {!!selected && (<View style={styles.checkmark}>
                  <Squircle width={rem(24)} height={rem(24)} color={colors.borders.iconStroke}>
                    <Squircle width={rem(20)} height={rem(20)} color={colors.elements.brand}>
                      <IconCheck alt color={colors.elements.white} width={10} height={10}/>
                    </Squircle>
                  </Squircle>
                </View>)}
            </View>) : null}
          {content || (<SettingsItemContent style={styles.contentContainer}>
              {topDescription ? (<SettingsItemSubDescription>
                  {topDescription}
                </SettingsItemSubDescription>) : null}
              <SettingsItemTitle {...titleProps}>{title}</SettingsItemTitle>
              {description ? (<SettingsItemDescription style={descriptionStyle} {...descriptionProps}>
                  {description}
                </SettingsItemDescription>) : null}
            </SettingsItemContent>)}
          {!!rightContent && rightContent}
        </View>
      </TouchableHighlight>);
});
export const SettingsItem = ({ ...props }) => <SettingsItemBase {...props}/>;
export const SettingsItemSwitch = ({ onValueChange, itemKey, value, isFetching, ...props }) => {
    const styles = useStyles(settingsItemStyleSet);
    const { disabled, onPressOnDisabled } = props;
    const pressableWhenDisabled = !!disabled && !!onPressOnDisabled;
    return (<SettingsItemBase {...props} disabled={!!disabled && !pressableWhenDisabled} onPress={pressableWhenDisabled ? onPressOnDisabled : undefined}>
      {props.children}
      <SettingsItemRightContent style={styles.switchSettingRight}>
        <View>
          <Switch value={value} onValueChange={(value) => onValueChange?.(value, itemKey)} disabled={Boolean(disabled)}/>
          {pressableWhenDisabled ? (<TouchableWithoutFeedback onPress={onPressOnDisabled}>
              <View style={StyleSheet.absoluteFill}/>
            </TouchableWithoutFeedback>) : null}
          {!!isFetching && (<View style={styles.loadingIndicator}>
              <Indicator brand/>
            </View>)}
        </View>
      </SettingsItemRightContent>
    </SettingsItemBase>);
};
export const SettingsItemNavigate = ({ title, children, description, disabled, onPress, onPressOnDisabled, titleFirst, isSelected, scrambleTitle, scrambleDescription, isFetching, useNumbersFont, titleNumberOfLines = 1, subDescription, ...props }) => {
    const colors = useColors();
    const placeholder = typeof title === 'string' && title.endsWith('..');
    const titleRender = placeholder ? (<BlurAmountView enabled={scrambleTitle}>
      <SettingsItemTitle numberOfLines={titleNumberOfLines} color={colors.text.placeholder} useNumbersFont={useNumbersFont}>
        {title}
      </SettingsItemTitle>
    </BlurAmountView>) : (Boolean(title) && (<BlurAmountView enabled={scrambleTitle}>
        <SettingsItemTitle numberOfLines={titleNumberOfLines} useNumbersFont={useNumbersFont}>
          {title}
        </SettingsItemTitle>
      </BlurAmountView>));
    const descriptionRender = Boolean(description) && (<BlurAmountView enabled={scrambleDescription}>
      <SettingsItemDescription>{description}</SettingsItemDescription>
    </BlurAmountView>);
    return (<SettingsItemBase {...props} disabled={!!(disabled || isFetching) && !onPressOnDisabled} onPress={() => {
            if (!disabled && !isFetching) {
                onPress?.();
            }
            else if (onPressOnDisabled) {
                onPressOnDisabled();
            }
        }}>
      <SettingsItemContent testID="settings-item-navigate-content">
        {children}
        {titleFirst ? (<>
            {titleRender}
            {descriptionRender}
          </>) : (<>
            {descriptionRender}
            {titleRender}
          </>)}
        {!!subDescription &&
            (typeof subDescription === 'string' ? (<SettingsItemSubDescription>
              {subDescription}
            </SettingsItemSubDescription>) : (subDescription))}
      </SettingsItemContent>
      <SettingsItemRightContent>
        {!disabled &&
            (isFetching ? (<Indicator dark/>) : (<IconArrow direction={
                // eslint-disable-next-line no-nested-ternary
                typeof isSelected === 'boolean'
                    ? isSelected
                        ? 'up'
                        : 'down'
                    : 'right'}/>))}
      </SettingsItemRightContent>
    </SettingsItemBase>);
};
export const SettingsItemWithCheckBox = ({ title, description, disabled, onPress, selected, topDescription, type = 'single', ...props }) => {
    const styles = useStyles(settingsItemStyleSet);
    return (<SettingsItemBase {...props} disabled={!!disabled} onPress={onPress}>
      <SettingsItemContent testID="settings-item-with-checkbox">
        <>
          {!!topDescription && (<SettingsItemTitle>{topDescription}</SettingsItemTitle>)}
          <SettingsItemTitle>{title}</SettingsItemTitle>
          {description ? (<SettingsItemDescription>{description}</SettingsItemDescription>) : null}
        </>
      </SettingsItemContent>
      <SettingsItemRightContent style={styles.checkBoxContainer}>
        <RadioButton type={type} isChecked={selected}/>
      </SettingsItemRightContent>
    </SettingsItemBase>);
};
export const SettingsItemInput = React.forwardRef(({ description, onClearTextInput, style, onPress, centered, descriptionStyle, disabled, ...props }, ref) => {
    const { onChangeText: onChangeTextProp, subDescription } = props;
    const fallbackTextInputRef = useRef(null);
    const [text, setText] = useState('');
    const onChangeText = useCallback((inputtedText) => {
        setText(inputtedText);
        if (onChangeTextProp) {
            onChangeTextProp(inputtedText);
        }
    }, [onChangeTextProp]);
    const colors = useColors();
    const styles = useStyles(settingsItemStyleSet);
    const shouldShowCross = !disabled &&
        (Boolean(props.value) || (props.value === undefined && text !== '')) &&
        props.editable !== false;
    const Cross = useMemo(() => (<TouchableWithoutFeedback hitSlop={hitSlop} onPress={onClearTextInput
            ? () => {
                if (props.onChangeText) {
                    props.onChangeText('');
                    setText('');
                    if (ref) {
                        if ('current' in ref) {
                            ref.current?.focus();
                        }
                    }
                }
            }
            : () => {
                props.onChangeText?.('');
                setText('');
                if (ref) {
                    if ('current' in ref) {
                        ref.current?.clear();
                    }
                }
                else if (fallbackTextInputRef.current) {
                    fallbackTextInputRef.current.clear();
                    fallbackTextInputRef.current.focus();
                }
            }}>
          <View style={styles.closeIconContainer}>
            <IconClose style={styles.closeIcon}/>
          </View>
        </TouchableWithoutFeedback>), [
        onClearTextInput,
        props,
        ref,
        styles.closeIcon,
        styles.closeIconContainer,
    ]);
    return (<SettingsItemBase {...props} style={style} disabled={disabled} onPress={() => {
            if (!disabled) {
                if (ref && 'current' in ref) {
                    ref.current?.focus();
                }
                onPress?.();
            }
        }}>
        <SettingsItemContent>
          <SettingsItemDescription style={[!!centered && styles.centeredText, descriptionStyle]}>
            {description}
          </SettingsItemDescription>
          <TextInput style={[styles.textInput, !!centered && styles.centeredTextInput]} {...props} onChangeText={onChangeText} ref={ref ?? fallbackTextInputRef} placeholderTextColor={colors.text.placeholder} selectionColor={colors.text.selection} returnKeyType={isDesktop ? undefined : props.returnKeyType}/>
          {!!subDescription &&
            (typeof subDescription === 'string' ? (<SettingsItemSubDescription>
                {subDescription}
              </SettingsItemSubDescription>) : (subDescription))}
          {!!shouldShowCross && !!centered && (<View style={styles.absCrossContainer}>{Cross}</View>)}
        </SettingsItemContent>
        {!!shouldShowCross && !centered && (<SettingsItemRightContent>{Cross}</SettingsItemRightContent>)}
      </SettingsItemBase>);
});
export const SettingsItemInputMask = React.forwardRef(({ description, onClearTextInput, subDescription, customSubDescription, disabled, shouldUsePlaceholderColor, prefix, noMask = false, useNumbersFont = false, showClearOnZero = false, style, ...props }, ref) => {
    const colors = useColors();
    const styles = useStyles(settingsItemStyleSet);
    const showCross = !disabled &&
        !!props.value &&
        (showClearOnZero ||
            !('type' in props &&
                props.type === 'money' &&
                (props.value === '0' || props.value === '0.00')));
    const inputStyle = [
        styles.textInput,
        useNumbersFont && styles.textInputNumbers,
        shouldUsePlaceholderColor && styles.placeholder,
    ];
    return (<SettingsItemBase style={style} {...props} onPress={ref && 'current' in ref
            ? () => {
                if (ref?.current && 'current' in ref) {
                    if ('getElement' in ref.current) {
                        ref.current?.getElement()?.focus();
                    }
                    else {
                        ref.current?.focus();
                    }
                }
            }
            : undefined}>
        <SettingsItemContent>
          <SettingsItemDescription>{description}</SettingsItemDescription>
          <View style={styles.prefixContainer}>
            {prefix ? <Text Text-16>{prefix}</Text> : null}
            {noMask ? (<TextInput style={inputStyle} {...props} placeholderTextColor={colors.text.placeholder} selectionColor={colors.text.selection} ref={ref} returnKeyType={isDesktop ? undefined : props.returnKeyType}/>) : (<TextInputMask style={inputStyle} {...props} placeholderTextColor={colors.text.placeholder} selectionColor={colors.text.selection} ref={ref} returnKeyType={isDesktop ? undefined : props.returnKeyType}/>)}
          </View>
          {customSubDescription ??
            (Boolean(subDescription) && (<SettingsItemSubDescription>
                {subDescription}
              </SettingsItemSubDescription>))}
        </SettingsItemContent>
        {showCross ? (<SettingsItemRightContent>
            <TouchableWithoutFeedback onPress={onClearTextInput} hitSlop={hitSlop} testID="clear-input">
              <View style={styles.closeIconContainer}>
                <IconClose style={styles.closeIcon}/>
              </View>
            </TouchableWithoutFeedback>
          </SettingsItemRightContent>) : null}
      </SettingsItemBase>);
});
export const SettingsItemMoneyInput = React.forwardRef(({ description, onClearTextInput, subDescription, customSubDescription, disabled, shouldUsePlaceholderColor, useNumbersFont = false, showClearOnZero = false, style, ...props }, ref) => {
    const colors = useColors();
    const styles = useStyles(settingsItemStyleSet);
    const showCross = !disabled &&
        !!props.value &&
        (showClearOnZero || Number(props.value) !== 0);
    const inputStyle = [
        styles.textInput,
        useNumbersFont && styles.textInputNumbers,
        shouldUsePlaceholderColor && styles.placeholder,
    ];
    return (<SettingsItemBase style={style} {...props} onPress={ref && 'current' in ref
            ? () => {
                ref.current?.focus();
            }
            : undefined}>
        <SettingsItemContent>
          <SettingsItemDescription>{description}</SettingsItemDescription>
          <View style={styles.prefixContainer}>
            <MoneyTextInput style={inputStyle} {...props} placeholderTextColor={colors.text.placeholder} selectionColor={colors.text.selection} ref={ref} returnKeyType={isDesktop ? undefined : props.returnKeyType}/>
          </View>
          {customSubDescription ??
            (Boolean(subDescription) && (<SettingsItemSubDescription>
                {subDescription}
              </SettingsItemSubDescription>))}
        </SettingsItemContent>
        {showCross ? (<SettingsItemRightContent>
            <TouchableWithoutFeedback onPress={onClearTextInput} hitSlop={hitSlop} testID="clear-input">
              <View style={styles.closeIconContainer}>
                <IconClose style={styles.closeIcon}/>
              </View>
            </TouchableWithoutFeedback>
          </SettingsItemRightContent>) : null}
      </SettingsItemBase>);
});
export const settingsItemStyleSet = createStyleSheets((colors) => ({
    container: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        margin: rem(16),
        ...(isWeb && {
            marginHorizontal: rem(8),
        }),
    },
    contentContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    rightContentContainer: {
        alignItems: 'flex-end',
        minWidth: rem(20),
    },
    iconContainer: {
        flex: 1,
        marginRight: rem(16),
    },
    textInput: {
        fontFamily: 'Montserrat-Regular',
        fontSize: rem(16),
        // iOS draws a TextInput's text at the bottom of its line height (React Native
        // doesn't centre it as it does for Text), so a line height above the font's
        // own (~19.5 pt at 16 pt) puts typed text below the placeholder. iOS gets
        // none; the height keeps the row at 24.
        ...(isIOS ? { height: rem(24) } : { lineHeight: rem(24) }),
        margin: 0,
        flex: 1,
        padding: 0,
        color: colors.text.primary,
        ...(isWeb && {
            caretColor: colors.text.selection,
        }),
    },
    textInputNumbers: {
        fontFamily: 'Nunito-Bold',
    },
    placeholder: {
        color: colors.text.placeholder,
    },
    closeIconContainer: {
        paddingVertical: rem(4),
        paddingHorizontal: rem(8),
        cursor: 'pointer',
        marginRight: -rem(8),
    },
    closeIcon: {
        color: colors.elements.secondary,
    },
    switchSettingRight: {
        marginLeft: rem(12),
    },
    checkmark: {
        right: 0,
        bottom: 0,
        width: rem(24),
        height: rem(24),
        position: 'absolute',
    },
    loadingIndicator: {
        ...StyleSheet.absoluteFillObject,
        left: -1,
        right: -1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    prefixContainer: { flexDirection: 'row', alignItems: 'center' },
    centeredText: {
        textAlign: 'center',
    },
    centeredTextInput: {
        textAlign: 'center',
        paddingHorizontal: rem(16),
    },
    absCrossContainer: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: rem(20),
        justifyContent: 'flex-end',
    },
    checkBoxContainer: {
        marginLeft: rem(16),
    },
}));
